import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Play, Pause, SkipForward, SkipBack, Heart, Film, ChevronRight } from "lucide-react";
import { API, BACKEND_URL } from "../App";

const SDE_TYPE_LABELS = {
  groom: "Groom's Story",
  bride: "Bride's Story",
  best_man: "Best Man Moments",
  maid_of_honor: "Maid of Honor Memories",
};

export default function SDEViewerPage() {
  const { slug, sdeType } = useParams();
  const [wedding, setWedding] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentClipIndex, setCurrentClipIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const wRes = await axios.get(`${API}/weddings/slug/${slug}`);
        setWedding(wRes.data);
        const sdeRes = await axios.get(`${API}/sde/public/${wRes.data.wedding_id}`);
        const match = sdeRes.data.find(p => p.sde_type === sdeType);
        if (match && match.clips?.length > 0) {
          setProject(match);
        } else {
          setError("No clips available for this perspective yet.");
        }
      } catch {
        setError("Could not load this experience.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, sdeType]);

  const getMediaUrl = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

  const clips = project?.clips || [];
  const currentClip = clips[currentClipIndex];

  const handleClipEnd = () => {
    if (currentClipIndex < clips.length - 1) {
      setCurrentClipIndex(prev => prev + 1);
    } else {
      setIsPlaying(false);
    }
  };

  const playAll = () => {
    setCurrentClipIndex(0);
    setIsPlaying(true);
  };

  useEffect(() => {
    if (videoRef.current && isPlaying) {
      videoRef.current.play().catch(() => {});
    }
  }, [currentClipIndex, isPlaying]);

  if (loading) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center">
        <div className="w-16 h-16 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Film className="w-16 h-16 text-wedding-gold/30 mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-wedding-ivory mb-3">{SDE_TYPE_LABELS[sdeType] || "Perspective"}</h1>
          <p className="text-wedding-ivory/50 mb-8">This perspective is being curated. Check back soon for an exclusive look at the day.</p>
          <Link to={`/${slug}`} className="btn-secondary inline-block" data-testid="sde-back-btn">Back to Wedding</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wedding-black" data-testid="sde-viewer-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-wedding-black/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/${slug}`} className="flex items-center gap-2 text-wedding-ivory/60 hover:text-wedding-gold transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="text-center">
            <h1 className="font-serif text-lg text-wedding-gold">{SDE_TYPE_LABELS[sdeType] || sdeType}</h1>
            <p className="text-wedding-ivory/40 text-xs">{project?.participant_name}</p>
          </div>
          <div className="w-16" />
        </div>
      </header>

      {/* Video Player */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          className="relative aspect-video bg-wedding-charcoal rounded-sm overflow-hidden mb-8"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {currentClip ? (
            <video
              ref={videoRef}
              key={currentClip.video_url}
              src={getMediaUrl(currentClip.video_url)}
              className="w-full h-full object-contain bg-black"
              controls
              onEnded={handleClipEnd}
              autoPlay={isPlaying}
              data-testid="sde-video-player"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film className="w-16 h-16 text-wedding-gold/30" />
            </div>
          )}
        </motion.div>

        {/* Playback Controls */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <button
            onClick={() => setCurrentClipIndex(Math.max(0, currentClipIndex - 1))}
            disabled={currentClipIndex === 0}
            className="p-3 rounded-full bg-wedding-charcoal border border-white/10 text-wedding-ivory/60 hover:text-wedding-gold hover:border-wedding-gold/30 disabled:opacity-30 transition-colors"
            data-testid="sde-prev-btn"
          >
            <SkipBack className="w-5 h-5" />
          </button>
          <button
            onClick={playAll}
            className="px-8 py-3 rounded-full bg-wedding-gold text-wedding-black font-bold text-sm uppercase tracking-widest hover:bg-wedding-ivory transition-colors"
            data-testid="sde-play-all-btn"
          >
            <Play className="w-5 h-5 inline mr-2" />
            Play All
          </button>
          <button
            onClick={() => setCurrentClipIndex(Math.min(clips.length - 1, currentClipIndex + 1))}
            disabled={currentClipIndex >= clips.length - 1}
            className="p-3 rounded-full bg-wedding-charcoal border border-white/10 text-wedding-ivory/60 hover:text-wedding-gold hover:border-wedding-gold/30 disabled:opacity-30 transition-colors"
            data-testid="sde-next-btn"
          >
            <SkipForward className="w-5 h-5" />
          </button>
        </div>

        {/* Clip Timeline */}
        <div>
          <p className="text-wedding-ivory/50 text-sm mb-4 uppercase tracking-wider">
            Clip {currentClipIndex + 1} of {clips.length}
          </p>
          <div className="grid grid-cols-6 sm:grid-cols-10 gap-2">
            {clips.map((clip, i) => (
              <button
                key={clip.slot_number}
                onClick={() => { setCurrentClipIndex(i); setIsPlaying(false); }}
                className={`aspect-square rounded-sm border-2 transition-all text-xs font-bold flex items-center justify-center ${
                  i === currentClipIndex
                    ? "border-wedding-gold bg-wedding-gold/20 text-wedding-gold"
                    : "border-white/10 bg-wedding-charcoal text-wedding-ivory/40 hover:border-wedding-gold/30"
                }`}
                data-testid={`sde-clip-${clip.slot_number}`}
              >
                {clip.slot_number}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/5">
        <p className="text-wedding-gold/60 text-sm">MemoraOrbs</p>
      </footer>
    </div>
  );
}
